import { useState } from "react";
import { motion } from "framer-motion";
import {
  Target,
  TrendingUp,
  Zap,
  Sparkles,
  Mail,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import "react-phone-number-input/style.css";
import { API_BASE_URL } from "@/lib/apiInterceptor";

const APPLICATION_NAME = "InsightSphere";

/* ─────────────────────────── Contact Form ─────────────────────────── */
const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[e.target.name];
        return next;
      });
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (!form.email.trim()) {
      next.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = "Enter a valid email";
    }
    // if (!form.message.trim()) next.message = "Message is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setSubmitError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
        ...(form.company ? { company: form.company.trim() || undefined } : {}),
        ...(form.phoneNumber ? { phoneNumber: form.phoneNumber || undefined } : {}),
        ...(form.message ? { message: form.message.trim() || undefined } : {}),
          applicationName: APPLICATION_NAME,
        }),
      });
      if (!response.ok) {
        let detail = `Request failed (${response.status})`;
        // try {
        //   const payload = await response.json();
        //   if (payload?.detail) detail = String(payload.detail);
        // } catch {
        //   /* ignore */
        // }
        throw new Error(detail);
      }
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        company: "",
        message: "",
        phoneNumber: "",
      });
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        className="flex flex-col items-center text-center py-12 gap-4 "
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="w-16 h-16 rounded-full gradient-bg flex items-center justify-center shadow-lg">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900">Request Submitted!</h3>
        <p className="text-gray-500 max-w-sm">
          Thanks for reaching out. Our team will get back to you within one
          business day.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="btn-primary mt-2 px-6 py-2.5"
        >
          Send another Request
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Jane Smith"
            className="input-field"
          />
          {errors.name && (
            <p className="text-xs text-red-500 mt-1">{errors.name}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
            Company
          </label>
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={handleChange}
            placeholder="Acme Corp"
            className="input-field"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
          Work Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="jane@company.com"
          className="input-field"
        />
        {errors.email && (
          <p className="text-xs text-red-500 mt-1">{errors.email}</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
          Contact Number
        </label>
        <div className="flex gap-2 mt-2">
           <input
          type="tel"
          name="phoneNumber"
          value={form.phoneNumber}
          onChange={handleChange}
          placeholder="123-456-7890"
          className="input-field"
        />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
          Message
        </label>
        <textarea
          name="message"
          rows={4}
          value={form.message}
          onChange={handleChange}
          placeholder="Tell us about your use case..."
          className="input-field resize-none"
        />
        {errors.message && (
          <p className="text-xs text-red-500 mt-1">{errors.message}</p>
        )}
      </div>

      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full py-3 text-base disabled:opacity-70"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-4 w-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
            Submitting
          </span>
        ) : (
          <>Submit</>
        )}
      </button>
    </form>
  );
};

/* ─────────────────────────── Landing Page ─────────────────────────── */
const LandingPage = () => {
  const scrollToContact = () => {
    document
      .getElementById("contact-section")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const features = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "AI-Powered Analysis",
      description:
        "Advanced AI agents analyze prospects and generate strategic insights",
      bgColor: "bg-blue-500",
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Real-time Simulation",
      description: "Watch AI agents engage in realistic sales conversations",
      bgColor: "bg-purple-500",
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Strategic Playbooks",
      description: "Generate actionable sales playbooks with quantified ROI",
      bgColor: "hsl(15 83% 56%)",
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Smart Routing",
      description: "Intelligent routing between sales and technical agents",
      bgColor: "bg-green-500",
    },
  ];

  const steps = [
    {
      step: "1",
      title: "Configure Your Setup",
      description:
        "Enter your sales company details and prospect information, or upload knowledge bases",
    },
    {
      step: "2",
      title: "Generate Sales Pitch",
      description:
        "AI agents analyze your knowledge bases and generate a comprehensive sales pitch",
    },
    {
      step: "3",
      title: "Simulate Conversation",
      description:
        "Watch AI agents engage in a realistic 6-turn conversation with intelligent routing",
    },
    {
      step: "4",
      title: "Get Strategic Playbook",
      description:
        "Receive a detailed sales playbook with pain points, talking points, and next steps",
    },
  ];

  return (
    <div className="w-full min-h-screen">
      {/* ── Page Body ── */}
      <div className="w-full px-6 lg:px-12 py-16">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-4xl mx-auto"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-black leading-tight">
            InsightSphere Intelligence Platform
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Combines seller knowledge, prospect intelligence, and decision-maker
            insights with Agentic AI to generate pitch strategies, deal risks,
            and conversation insights
          </p>
          <div className="flex justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToContact}
              className="btn-simulation text-lg px-8 py-4"
            >
              <Mail className="w-5 h-5" />
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="card hover:shadow-2xl transition-all duration-300 group"
            >
              <div
                className={`w-16 h-16 rounded-2xl p-4 text-white mb-4 group-hover:scale-110 transition-transform shadow-lg ${
                  feature.bgColor.startsWith("hsl") ? "" : feature.bgColor
                }`}
                style={
                  feature.bgColor.startsWith("hsl")
                    ? { backgroundColor: feature.bgColor }
                    : {}
                }
              >
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold gradient-text mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>
         <div className="max-w-7xl mx-auto flex flex-row justify-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative w-full overflow-hidden border-1 rounded-sm bg-black min-h-[320px] lg:min-h-full col-span-2 mb-5"
        >
          <video
            className="w-full h-full"
            src="https://fission-ai-media.s3.us-east-1.amazonaws.com/insightsphere/InsightSphereDemo.mp4"
            poster="https://fission-ai-media.s3.us-east-1.amazonaws.com/insightsphere/insightsphere.png"
            controls
            preload="metadata"
            muted
          >
            Your browser does not support the video tag.
          </video>
          <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-xs font-semibold text-gray-800 shadow">
            Product Preview
          </div>
        </motion.div>
        </div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="card max-w-4xl mx-auto"
        >
          <h2 className="text-3xl font-bold text-center mb-8">
            <span className="text-black">How It </span>
            <span style={{ color: "hsl(15 83% 56%)" }}>Works</span>
          </h2>
          <div className="space-y-6">
            {steps.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="flex-shrink-0 w-12 h-12 gradient-bg rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-lg font-semibold gradient-text mb-1">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ── Contact Section: Video + Form ── */}
        <motion.div
          id="contact-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-7xl mx-auto mt-24 scroll-mt-24"
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-3">
              Ready to transform your{" "}
              <span style={{ color: "hsl(15 83% 56%)" }}>sales process?</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Watch a quick preview and talk to our team to see how
              InsightSphere can accelerate your pipeline today.
            </p>
          </div>

          <div className="flex flex-row justify-center  gap-8 items-stretch">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="card w-[800px]"
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  Request
                </h3>
                <p className="text-gray-500 text-sm">
                  Tell us about your sales intelligence needs and we'll reach
                  out shortly.
                </p>
              </div>
              <ContactForm />
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* ── Footer ── */}
      <footer className="w-full py-6 px-6 border-t border-gray-100 text-center text-sm text-gray-500 mt-8">
        © {new Date().getFullYear()} InsightSphere Intelligence Platform. All
        rights reserved.
      </footer>
    </div>
  );
};

export default LandingPage;
